import { z } from "zod";

export const syncRecordKindSchema = z.enum([
  "tenant_settings",
  "store_public_config",
  "store_ruten_publish_config",
  "collected_product",
  "product_curation",
  "publish_preparation",
  "store_order",
  "store_conversation",
  "store_message",
  "store_sync_state",
  "rule_evaluation_summary"
]);

export const encryptedSyncBlobKindSchema = z.enum([
  "store_credential_v1",
  "store_proxy_secret_v1",
  "browser_profile_archive_v1"
]);

export const syncCipherSchema = z.enum(["xchacha20poly1305"]);

export const syncDeviceStatusSchema = z.enum(["active", "revoked"]);
export const encryptedSyncBlobStorageModeSchema = z.enum(["inline", "chunked"]);

const syncIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(128)
  .refine((value) => !isSensitiveSyncStringValue(value), {
    message: "同步 ID 不能包含本地路径"
  });
const syncTenantIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(128)
  .refine((value) => !isSensitiveSyncStringValue(value), {
    message: "同步用户 ID 不能包含本地路径"
  });
const syncStoreIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(128)
  .refine((value) => !isSensitiveSyncStringValue(value), {
    message: "同步店铺 ID 不能包含本地路径"
  });
const syncResourceIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .refine((value) => !isSensitiveSyncStringValue(value), {
    message: "同步资源 ID 不能包含本地路径"
  });
const syncDateTimeSchema = z
  .string()
  .trim()
  .min(1)
  .refine((value) => Number.isFinite(new Date(value).getTime()), {
    message: "日期时间不合法"
  });
const syncBase64Schema = z.string().trim().min(1).max(16 * 1024 * 1024);
const syncHashSchema = z.string().trim().regex(/^[a-f0-9]{64}$/i);

export const syncSensitiveKeyMarkers = [
  "confirmationtoken",
  "accesstoken",
  "refreshtoken",
  "token",
  "authorization",
  "cookie",
  "password",
  "secret",
  "apikey",
  "privatekey",
  "salt",
  "storagepath",
  "profilepath",
  "userdatadir",
  "chromepath",
  "browserexecutablepath",
  "executablepath",
  "wsendpoint",
  "localimagepath",
  "proxypassword",
  "proxyurl",
  "plaintext",
  "ciphertext",
  "nonce"
] as const;

export function isSensitiveSyncKey(key: string): boolean {
  const normalized = key
    .split("")
    .filter((char) => /[a-z0-9]/i.test(char))
    .join("")
    .toLowerCase();
  if (normalized === "ck") return true;
  return syncSensitiveKeyMarkers.some((marker) => normalized.includes(marker));
}

export function findSensitiveSyncPath(value: unknown): string | null {
  return findSensitiveSyncPathAt(value, "$", 0);
}

function findSensitiveSyncPathAt(value: unknown, path: string, depth: number): string | null {
  if (depth > 24) return path;
  if (typeof value === "string") {
    return isSensitiveSyncStringValue(value) ? path : null;
  }
  if (!value || typeof value !== "object") return null;
  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index += 1) {
      const found = findSensitiveSyncPathAt(value[index], `${path}[${index}]`, depth + 1);
      if (found) return found;
    }
    return null;
  }
  for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
    const nestedPath = `${path}.${key}`;
    if (isSensitiveSyncKey(key)) return nestedPath;
    const found = findSensitiveSyncPathAt(nested, nestedPath, depth + 1);
    if (found) return found;
  }
  return null;
}

export function isSensitiveSyncStringValue(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  const normalized = trimmed.replace(/\\/g, "/");
  const lower = normalized.toLowerCase();
  if (lower.startsWith("file://")) return true;
  if (lower.includes(".local-data/")) return true;
  if (lower.includes("/appdata/local/") || lower.includes("/appdata/roaming/")) return true;
  if (lower.includes("/users/") && lower.includes("/appdata/")) return true;
  if (lower.includes("/browser-profile/") || lower.includes("/browser-profiles/")) return true;
  if (lower.includes("/artifacts/") && /(^|[/])\.local-data[/]/.test(lower)) return true;
  if (/^[a-z]:\//i.test(normalized)) return true;
  if (/^(\/users|\/home|\/var\/folders|\/tmp)\//i.test(normalized)) return true;
  return false;
}

const syncPlainContentSchema = z
  .record(z.unknown())
  .superRefine((value, ctx) => {
    const sensitivePath = findSensitiveSyncPath(value);
    if (sensitivePath) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `同步明文数据包含敏感字段：${sensitivePath}`
      });
    }
  });

export const syncRecordSchema = z.object({
  id: syncIdSchema.optional(),
  tenantId: syncTenantIdSchema,
  storeId: syncStoreIdSchema.nullish(),
  kind: syncRecordKindSchema,
  resourceId: syncResourceIdSchema,
  version: z.number().int().min(1),
  schemaVersion: z.number().int().min(1).max(100),
  updatedAt: syncDateTimeSchema,
  deletedAt: syncDateTimeSchema.nullish(),
  updatedByDeviceId: syncIdSchema,
  content: syncPlainContentSchema,
  contentHash: syncHashSchema
});

export const syncDeviceSchema = z.object({
  id: syncIdSchema,
  tenantId: syncTenantIdSchema,
  deviceId: syncIdSchema,
  deviceName: z.string().trim().min(1).max(160).optional(),
  publicKeyJwk: z.record(z.unknown()).optional(),
  status: syncDeviceStatusSchema,
  trustedAt: syncDateTimeSchema,
  revokedAt: syncDateTimeSchema.nullish(),
  lastSeenAt: syncDateTimeSchema
});

export const encryptedSyncBlobAadSchema = z
  .object({
    tenantId: syncTenantIdSchema,
    storeId: syncStoreIdSchema.nullish(),
    kind: encryptedSyncBlobKindSchema,
    resourceId: syncResourceIdSchema,
    version: z.number().int().min(1),
    deviceId: syncIdSchema
  })
  .strict();

export const encryptedSyncBlobSchema = z.object({
  id: syncIdSchema.optional(),
  tenantId: syncTenantIdSchema,
  storeId: syncStoreIdSchema.nullish(),
  kind: encryptedSyncBlobKindSchema,
  resourceId: syncResourceIdSchema,
  version: z.number().int().min(1),
  schemaVersion: z.number().int().min(1).max(100),
  cipher: syncCipherSchema,
  storageMode: encryptedSyncBlobStorageModeSchema.default("inline"),
  nonceB64: syncBase64Schema.max(512),
  aad: encryptedSyncBlobAadSchema,
  ciphertextB64: syncBase64Schema.optional(),
  chunkSizeBytes: z.number().int().min(1).max(16 * 1024 * 1024).nullish(),
  chunkCount: z.number().int().min(1).max(10000).nullish(),
  ciphertextSha256: syncHashSchema,
  sizeBytes: z.number().int().min(1).max(1024 * 1024 * 1024),
  updatedAt: syncDateTimeSchema,
  deletedAt: syncDateTimeSchema.nullish(),
  updatedByDeviceId: syncIdSchema
}).superRefine((value, ctx) => {
  if (
    value.aad.tenantId !== value.tenantId ||
    (value.aad.storeId ?? null) !== (value.storeId ?? null) ||
    value.aad.kind !== value.kind ||
    value.aad.resourceId !== value.resourceId ||
    value.aad.version !== value.version ||
    value.aad.deviceId !== value.updatedByDeviceId
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "密文 AAD 与同步 blob 元数据不匹配"
    });
  }
  if (value.storageMode === "inline" && !value.ciphertextB64) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["ciphertextB64"],
      message: "内联同步密文缺少 ciphertextB64"
    });
  }
  if (value.storageMode === "chunked" && (!value.chunkCount || !value.chunkSizeBytes)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["chunkCount"],
      message: "分片同步密文缺少 chunkCount 或 chunkSizeBytes"
    });
  }
});

export const encryptedSyncBlobChunkSchema = z.object({
  tenantId: syncTenantIdSchema,
  blobId: syncIdSchema,
  deviceId: syncIdSchema,
  chunkIndex: z.number().int().min(0).max(9999),
  chunkCount: z.number().int().min(1).max(10000),
  ciphertextChunkB64: syncBase64Schema,
  chunkSha256: syncHashSchema
}).superRefine((value, ctx) => {
  if (value.chunkIndex >= value.chunkCount) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["chunkIndex"],
      message: "同步密文分片序号超出分片数量"
    });
  }
});

export const encryptedSyncBlobChunkListRequestSchema = z.object({
  deviceId: syncIdSchema
});

/**
 * 服务端托管 root_key 的响应（Server-Held Root Key 模型）。
 *
 * - rootKeyB64：32 字节 base64；调用方仅在内存中持有，不允许落盘 / 日志 / 审计 metadata。
 * - version：root_key 当前版本，admin rotate 后递增；旧 blob 仍由旧 version 解密。
 * - masterKeyId：服务端 master KEK 标识，便于审计排查；不要据此猜测 KEK 字节。
 */
export const syncRootKeyResponseSchema = z.object({
  rootKey: z.object({
    version: z.number().int().min(1),
    masterKeyId: z.string().trim().min(1).max(64),
    rootKeyB64: z.string().min(43).max(64),
    createdAt: syncDateTimeSchema,
    rotatedAt: syncDateTimeSchema.nullish()
  })
});

export const syncPullRequestSchema = z.object({
  tenantId: syncTenantIdSchema,
  deviceId: syncIdSchema,
  cursor: z.string().trim().min(1).max(512).nullish(),
  kinds: z.array(z.union([syncRecordKindSchema, encryptedSyncBlobKindSchema])).max(100).optional(),
  limit: z.number().int().min(1).max(500).default(200)
});

export const syncPushRequestSchema = z.object({
  tenantId: syncTenantIdSchema,
  deviceId: syncIdSchema,
  baseCursor: z.string().trim().min(1).max(512).nullish(),
  records: z.array(syncRecordSchema).max(500).default([]),
  encryptedBlobs: z.array(encryptedSyncBlobSchema).max(100).default([])
}).superRefine((value, ctx) => {
  for (const [index, record] of value.records.entries()) {
    if (record.tenantId !== value.tenantId || record.updatedByDeviceId !== value.deviceId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["records", index],
        message: "同步记录账号主体或设备不匹配"
      });
    }
  }
  for (const [index, blob] of value.encryptedBlobs.entries()) {
    if (blob.tenantId !== value.tenantId || blob.updatedByDeviceId !== value.deviceId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["encryptedBlobs", index],
        message: "同步密文账号主体或设备不匹配"
      });
    }
  }
});

export const syncPushResponseSchema = z.object({
  cursor: z.string().trim().min(1),
  acceptedRecordCount: z.number().int().min(0),
  acceptedBlobCount: z.number().int().min(0),
  /** Server-Wins：被服务端拒绝的旧版本数量（仅供 audit 与调试，UI 不展示）。 */
  rejectedStaleCount: z.number().int().min(0).default(0)
});

export const syncPullResponseSchema = z.object({
  cursor: z.string().trim().min(1),
  records: z.array(syncRecordSchema),
  encryptedBlobs: z.array(encryptedSyncBlobSchema)
});

export const syncDeviceRegisterSchema = z.object({
  tenantId: syncTenantIdSchema,
  deviceId: syncIdSchema,
  deviceName: z.string().trim().min(1).max(160).optional(),
  publicKeyJwk: z.record(z.unknown()).optional()
});

export const syncDeviceRevokeSchema = z.object({
  tenantId: syncTenantIdSchema,
  deviceId: syncIdSchema
});

export type SyncRecordKind = z.infer<typeof syncRecordKindSchema>;
export type EncryptedSyncBlobKind = z.infer<typeof encryptedSyncBlobKindSchema>;
export type SyncCipher = z.infer<typeof syncCipherSchema>;
export type EncryptedSyncBlobStorageMode = z.infer<typeof encryptedSyncBlobStorageModeSchema>;
export type SyncRecord = z.infer<typeof syncRecordSchema>;
export type SyncDevice = z.infer<typeof syncDeviceSchema>;
export type EncryptedSyncBlob = z.infer<typeof encryptedSyncBlobSchema>;
export type EncryptedSyncBlobChunk = z.infer<typeof encryptedSyncBlobChunkSchema>;
export type SyncRootKeyResponse = z.infer<typeof syncRootKeyResponseSchema>;
export type SyncPullRequest = z.infer<typeof syncPullRequestSchema>;
export type SyncPushRequest = z.infer<typeof syncPushRequestSchema>;
export type SyncPushResponse = z.infer<typeof syncPushResponseSchema>;
export type SyncPullResponse = z.infer<typeof syncPullResponseSchema>;
export type SyncDeviceRegisterInput = z.infer<typeof syncDeviceRegisterSchema>;
export type SyncDeviceRevokeInput = z.infer<typeof syncDeviceRevokeSchema>;
